import React, { useState } from "react";
import { BookOpen, Plus, Edit2, Trash2 } from "lucide-react";
import DynamicList from "../../../components/dynamic/List";
import { ApiService } from "../../../services/api";
import Modal from "../../../components/ui/Modal";
import ConfirmModal from "../../../components/modals/ConfirmModal";

type PlanningLesson = {
  id: string;
  name: string;
  code?: string;
  duration?: number;
  description?: string;
  isActive?: boolean;
};

const LessonsList = ({ hidePageHeading = false }: { hidePageHeading?: boolean }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<PlanningLesson | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    duration: 40,
  });

  const fetchPlanningLessons = async (page: number, searchTerm?: string) => {
    const res = await ApiService.getPlanningLessons({
      page,
      limit: 20,
      search: searchTerm && searchTerm.trim() ? searchTerm : undefined,
      sortBy: "lessonName",
      sortOrder: "ASC",
    });
    const normalized: any = (res as any)?.data ?? (res as any) ?? {};
    const results = normalized.results || [];
    const mappedData = results.map((item: any) => ({
      id: item.id,
      name: item.lessonName || item.name,
      code: item.lessonCode || item.code,
      duration: item.lessonDuration || item.duration,
      isActive: item.isActive,
      description: item.description,
    }));

    return {
      data: mappedData as PlanningLesson[],
      meta: {
        page: Number(normalized.page || 1),
        totalPages: Number(normalized.totalPages || 1),
        totalItems: Number(normalized.totalResults || 0),
        limit: Number(normalized.limit || 20),
      },
    };
  };

  const handleCreate = () => {
    setSelectedLesson(null);
    setFormData({ name: "", code: "", duration: 40 });
    setIsModalOpen(true);
  };

  const handleEdit = (lesson: PlanningLesson) => {
    setSelectedLesson(lesson);
    setFormData({
      name: lesson.name,
      code: lesson.code || "",
      duration: lesson.duration || 40,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (lesson: PlanningLesson) => {
    setSelectedLesson(lesson);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (selectedLesson) {
        await ApiService.updatePlanningLesson(selectedLesson.id, {
          lessonName: formData.name,
          lessonCode: formData.code,
          lessonDuration: Number(formData.duration),
        });
      } else {
        await ApiService.createPlanningLesson({
          lessonName: formData.name,
          lessonCode: formData.code,
          lessonDuration: Number(formData.duration),
        });
      }
      setIsModalOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving lesson:", error);
      setErrorMessage("Ders kaydedilirken bir hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedLesson) return;
    try {
      await ApiService.deletePlanningLesson(selectedLesson.id);
      setIsDeleteModalOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting lesson:", error);
      setErrorMessage("Silme işlemi başarısız oldu.");
      setErrorModalOpen(true);
    }
  };

  const renderItem = (lesson: PlanningLesson) => {
    return (
      <div
        className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
        key={lesson.id}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-gray-900">{lesson.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span className="rounded-md bg-gray-100 px-2 py-0.5">Kod: {lesson.code || "-"}</span>
                <span className="rounded-md bg-gray-100 px-2 py-0.5">Süre: {lesson.duration || 40} dk</span>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => handleEdit(lesson)}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
              title="Düzenle"
            >
              <Edit2 size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(lesson)}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
              title="Sil"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {!hidePageHeading && (
          <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Dersler</h2>
              <p className="mt-0.5 text-sm text-gray-500">Planlamada kullanılacak dersleri tanımlayın</p>
            </div>
          </div>
        )}
        <DynamicList
          embedded
          header={false}
          title="Dersler"
          isSearch={true}
          isPagination={true}
          path="planning/lessons"
          fetcher={fetchPlanningLessons}
          refreshKey={refreshKey}
          item={(row) => renderItem(row as PlanningLesson)}
          showToggle={false}
          emptyMessage="Henüz ders eklenmemiş."
          actions={
            <button
              type="button"
              onClick={handleCreate}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-transparent bg-blue-600 px-3.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 sm:px-4"
            >
              <Plus size={16} className="shrink-0" />
              <span>Yeni Ders Ekle</span>
            </button>
          }
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedLesson ? "Dersi Düzenle" : "Yeni Ders Ekle"}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ders Adı</label>
            <input
              required
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              placeholder="Örn: Matematik"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ders Kodu</label>
              <input
                required
                type="text"
                maxLength={6}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="Örn: MAT"
              />
              <p className="text-xs text-gray-500 mt-1">Maksimum 6 karakter</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Süre (Dakika)</label>
              <input
                required
                type="number"
                min={1}
                max={180}
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {formLoading ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Dersi Sil"
        message={`"${selectedLesson?.name}" dersini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        confirmButtonVariant="danger"
      />

      {/* Error Modal */}
      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />
    </>
  );
};

export default LessonsList;